import type { ProcessedToolkit } from "@/clients/arcade/arcade";

/**
 * A ToolkitGroup represents a primary toolkit and its associated unoptimized/starter toolkit.
 *
 * For example, "Gmail" (primary) may be grouped with "GmailApi" (starter/unoptimized).
 * For now, unoptimized toolkits are always shown. In the future, they will not be.
 * When the unoptimized toolkits toggle is off, only primary tools are shown.
 * When on, both primary and unoptimized/starter tools are included.
 */
export type ToolkitGroup = {
  /** The primary (non-starter) toolkit, or the starter toolkit if no primary exists */
  primary: ProcessedToolkit;
  /** Associated unoptimized/starter toolkit (null if none) */
  starterToolkit: ProcessedToolkit | null;
  /** True if this group has no primary toolkit (starter-only) */
  isStarterOnly: boolean;
  /** Display name for the group (uses primary toolkit name) */
  displayName: string;
};

/**
 * Check if a toolkit is an unoptimized/starter toolkit based on metadata type
 */
export const isStarterToolkit = (toolkit: ProcessedToolkit): boolean =>
  toolkit.metadata?.type === "arcade_starter";

/**
 * Regex to extract base name from starter toolkit names.
 * Matches pattern: {BaseName}Api (case-insensitive)
 * Examples: "SlackApi" -> "Slack", "SlackAPI" -> "Slack", "HubspotCrmApi" -> "HubspotCrm"
 */
const API_SUFFIX_REGEX = /^(.+?)api$/i;

/**
 * Regex to trim the last CamelCase word segment for fallback matching.
 * Used when direct matching fails (e.g., "HubspotCrm" -> "Hubspot").
 * This handles cases where the starter name includes extra context
 * like "HubspotCrmApi" needing to match primary "Hubspot".
 */
const TRAILING_WORD_REGEX = /[A-Z][a-z]*$/;

/**
 * Extract the base name from a starter toolkit name.
 * Examples:
 *   - "SlackApi" -> "Slack"
 *   - "HubspotCrmApi" -> "HubspotCrm"
 *   - "GitHubApi" -> "GitHub"
 *
 * Returns null if no base name can be extracted.
 */
function extractBaseName(starterName: string): string | null {
  const apiSuffixMatch = starterName.match(API_SUFFIX_REGEX);
  if (apiSuffixMatch) {
    return apiSuffixMatch[1];
  }
  return null;
}

/**
 * Find the best matching primary toolkit for a starter toolkit.
 * Uses case-insensitive matching to handle variations like "Github" vs "GitHub".
 * First tries direct match, then falls back to trimming the last CamelCase segment.
 */
function findPrimaryToolkit(
  starterToolkit: ProcessedToolkit,
  allToolkits: ProcessedToolkit[]
): ProcessedToolkit | null {
  const baseName = extractBaseName(starterToolkit.name);
  if (!baseName) {
    return null;
  }

  const baseNameLower = baseName.toLowerCase();

  // Find a non-starter toolkit that matches the base name
  const directMatch =
    allToolkits.find(
      (tk) => !isStarterToolkit(tk) && tk.name.toLowerCase() === baseNameLower
    ) ?? null;
  if (directMatch) {
    return directMatch;
  }

  const trimmedBase = baseName.replace(TRAILING_WORD_REGEX, "");
  if (!trimmedBase || trimmedBase === baseName) {
    return null;
  }
  const trimmedLower = trimmedBase.toLowerCase();
  return (
    allToolkits.find(
      (tk) => !isStarterToolkit(tk) && tk.name.toLowerCase() === trimmedLower
    ) ?? null
  );
}

/**
 * Group toolkits into ToolkitGroups by pairing primary toolkits with their starter counterpart.
 *
 * Strategy:
 * 1. Identify all starter toolkits and try to match them to a primary
 * 2. Group matched starter with its primary
 * 3. Standalone starters (no matching primary) become their own starter-only groups
 * 4. Primary toolkits without starters become groups with null starterToolkit
 *
 * Ordering: Groups are returned with primary toolkits first (in input order),
 * followed by any unmatched starter-only groups (in input order).
 */
export function groupToolkits(toolkits: ProcessedToolkit[]): ToolkitGroup[] {
  // Separate starter and non-starter toolkits
  const starters: ProcessedToolkit[] = [];
  const primaries: ProcessedToolkit[] = [];

  for (const tk of toolkits) {
    if (isStarterToolkit(tk)) {
      starters.push(tk);
    } else {
      primaries.push(tk);
    }
  }

  // Map to track which primary has which starter
  const primaryToStarter = new Map<string, ProcessedToolkit | null>();
  const matchedStarterNames = new Set<string>();

  // Initialize map with all primaries (no starter yet)
  for (const primary of primaries) {
    primaryToStarter.set(primary.name, null);
  }

  // Match starters to primaries (first match wins)
  for (const starter of starters) {
    const primary = findPrimaryToolkit(starter, primaries);
    if (primary && !primaryToStarter.get(primary.name)) {
      primaryToStarter.set(primary.name, starter);
      matchedStarterNames.add(starter.name);
    }
  }

  // Build groups from primaries
  const groups: ToolkitGroup[] = [];

  for (const primary of primaries) {
    const starterToolkit = primaryToStarter.get(primary.name) ?? null;
    groups.push({
      primary,
      starterToolkit,
      isStarterOnly: false,
      displayName: primary.name,
    });
  }

  // Add unmatched starters as starter-only groups
  for (const starter of starters) {
    if (!matchedStarterNames.has(starter.name)) {
      groups.push({
        primary: starter,
        starterToolkit: null,
        isStarterOnly: true,
        displayName: starter.name,
      });
    }
  }

  return groups;
}

/**
 * Get the tool count for a toolkit group.
 * If includeStarter is true, includes tool count from the starter toolkit.
 */
export function getGroupToolCount(
  group: ToolkitGroup,
  includeStarter: boolean
): number {
  if (!includeStarter && group.isStarterOnly) {
    return 0;
  }
  let count = group.primary.tools.length;

  if (includeStarter && group.starterToolkit) {
    count += group.starterToolkit.tools.length;
  }

  return count;
}

/**
 * Check if a toolkit group has any selected tools.
 * When includeStarter is false, only checks primary toolkit tools.
 */
export function hasSelectedToolsInGroup(
  group: ToolkitGroup,
  selectedTools: Set<string>,
  includeStarter = true
): boolean {
  if (!includeStarter && group.isStarterOnly) {
    return false;
  }
  // Check primary toolkit tools
  if (
    group.primary.tools.some((t) => selectedTools.has(t.fully_qualified_name))
  ) {
    return true;
  }

  // Check starter toolkit tools only if includeStarter is true
  if (
    includeStarter &&
    group.starterToolkit &&
    group.starterToolkit.tools.some((t) =>
      selectedTools.has(t.fully_qualified_name)
    )
  ) {
    return true;
  }

  return false;
}

/**
 * Count selected tools in a group.
 */
export function countSelectedToolsInGroup(
  group: ToolkitGroup,
  selectedTools: Set<string>,
  includeStarter: boolean
): number {
  if (!includeStarter && group.isStarterOnly) {
    return 0;
  }
  let count = 0;

  for (const tool of group.primary.tools) {
    if (selectedTools.has(tool.fully_qualified_name)) {
      count += 1;
    }
  }

  if (includeStarter && group.starterToolkit) {
    for (const tool of group.starterToolkit.tools) {
      if (selectedTools.has(tool.fully_qualified_name)) {
        count += 1;
      }
    }
  }

  return count;
}
